import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { CHANGE_LOCATION } from 'redux-store/reducers/router-reducer';

import {
  distinctVideoTutorialList,
  getActivePageVideos,
  getDistinctVideoIds,
  getVideosForCurrentUser
} from '../util/videos';
import { createReducer } from 'utils/redux';
import { API, get, post } from 'utils/api';
import { logError } from 'utils/log';

import type { HistoryResponse, TutorialHistoryEntry } from '../types/HistoryResponse.type';
import type { Action, ThunkAction } from 'types';

const TUTORIAL_VIDEOS_LOADING = 'tutorialsVideos/LOADING';
const TUTORIAL_VIDEOS_LOADED = 'tutorialsVideos/LOADED';
const TUTORIAL_VIDEOS_SET_ACTIVE = 'tutorialsVideos/SET_ACTIVE';
const TUTORIAL_VIDEOS_SET_VIEWED = 'tutorialsVideos/SET_VIEWED';
const TUTORIAL_VIDEOS_RESET = 'tutorialsVideos/RESET';
const TUTORIAL_VIDEOS_SET_ORGANIZATION_TYPE = 'tutorialsVideos/SET_ORGANIZATION_TYPE';

const { TUTORIALS } = API.END_POINTS;

export type State = {
  history: Array<TutorialHistoryEntry>,
  isSelfService: boolean,
  isUserNewOrInactive: boolean,
  pageVideos: Array<string>,
  userVideos: Array<string>,
  activeVideo: string | undefined | null,
  currentPath: string,
  organizationType: string | undefined | null,
  isLoading: boolean
};

const initialState: State = {
  history: [],
  isSelfService: false,
  isUserNewOrInactive: false,
  pageVideos: [],
  userVideos: [],
  activeVideo: undefined,
  currentPath: '',
  organizationType: undefined,
  isLoading: false
};

function setLoadingStatus(status: boolean): Action {
  return {
    type: TUTORIAL_VIDEOS_LOADING,
    payload: {
      status
    }
  };
}

function historyLoaded(data: HistoryResponse): Action {
  return {
    type: TUTORIAL_VIDEOS_LOADED,
    payload: {
      data
    }
  };
}

export function resetHistory(): Action {
  return {
    type: TUTORIAL_VIDEOS_RESET
  };
}

function setViewed(videoKind: string): Action {
  return {
    type: TUTORIAL_VIDEOS_SET_VIEWED,
    payload: videoKind
  };
}

export function setOrganizationType(organizationType: string, isSelfService: boolean): Action {
  return {
    type: TUTORIAL_VIDEOS_SET_ORGANIZATION_TYPE,
    payload: {
      organizationType,
      isSelfService
    }
  };
}

function markAsViewed(videoKind?: string | null): ThunkAction {
  return (dispatch) => {
    const kind = videoKind; // make flow type-check happy
    if (!kind) {
      return Promise.reject();
    }

    const url = TUTORIALS.tutorialHistory();

    return post(url, {
      data: kind,
      headers: {
        'content-type': 'application/json'
      }
    })
      .then(() => {
        dispatch(setViewed(kind));
      })
      .catch((error) => {
        logError(error);
      });
  };
}

export function setActiveVideo(data?: string | null, forceUpdate: boolean = false): ThunkAction {
  return (dispatch) => {
    dispatch({
      type: TUTORIAL_VIDEOS_SET_ACTIVE,
      payload: {
        data,
        forceUpdate
      }
    });

    if (data) {
      const video = distinctVideoTutorialList
        .find((entry) => entry.id === data);
      dispatch(markAsViewed(video?.kind));
    }
  };
}

export function triggerVideo(videoKind: string): ThunkAction {
  return (dispatch, getState) => {
    const currentState = getState().tutorialVideos;
    const videoToPlay = distinctVideoTutorialList.find(entry => entry.kind === videoKind);

    if (!videoToPlay) {
      return;
    }
    const isInPageVideos = currentState.pageVideos.includes(videoToPlay.id);
    const wasVideoPlayed = Boolean(currentState.history
      .find((entry: TutorialHistoryEntry) => entry.type === videoKind && !entry.shouldBeWatched));
    const shouldBePlayed = (!wasVideoPlayed || currentState.isUserNewOrInactive) && isInPageVideos;

    if (shouldBePlayed) {
      dispatch(setActiveVideo(videoToPlay.id));
    }
  };
}

export function fetchTutorialHistory(): ThunkAction {
  return (dispatch) => {
    const url = TUTORIALS.tutorialHistory();

    dispatch(resetHistory());
    dispatch(setLoadingStatus(true));

    return get(url)
      .then((response) => {
        dispatch(historyLoaded(response.data));
      })
      .catch((error) => {
        dispatch(setLoadingStatus(false));
        logError(error);
      });
  };
}

export default createReducer<State, any>('tutorialVideos', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [CHANGE_LOCATION]: (state, { location }) => {
    const userVideos = getVideosForCurrentUser(state.organizationType, state.isSelfService);
    return {
      ...state,
      currentPath: location.path,
      userVideos: getDistinctVideoIds(userVideos),
      pageVideos: getDistinctVideoIds(getActivePageVideos(location.path, userVideos))
    };
  },
  [TUTORIAL_VIDEOS_SET_ORGANIZATION_TYPE]: (state, { organizationType, isSelfService }) => {
    const userVideos = getVideosForCurrentUser(organizationType, isSelfService);
    return {
      ...state,
      organizationType,
      isSelfService,
      userVideos: getDistinctVideoIds(userVideos),
      pageVideos: getDistinctVideoIds(getActivePageVideos(state.currentPath, userVideos))
    };
  },
  [TUTORIAL_VIDEOS_LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [TUTORIAL_VIDEOS_LOADED]: (state, { data }) => {
    const userVideos = getVideosForCurrentUser(state.organizationType, state.isSelfService);
    return {
      ...state,
      history: data.history,
      isUserNewOrInactive: data.newOrInactive,
      isLoading: false,
      userVideos: getDistinctVideoIds(userVideos),
      pageVideos: getDistinctVideoIds(getActivePageVideos(state.currentPath, userVideos))
    };
  },
  [TUTORIAL_VIDEOS_RESET]: (state) => ({
    ...state,
    history: []
  }),
  [TUTORIAL_VIDEOS_SET_ACTIVE]: (state, { data, forceUpdate }) => {
    let activeVideo = state.activeVideo;

    if (!data || forceUpdate) {
      // unset video is always forced
      activeVideo = data;
    } else {
      // set it only if it was empty
      activeVideo = activeVideo || data;
    }

    return {
      ...state,
      activeVideo
    };
  },
  [TUTORIAL_VIDEOS_SET_VIEWED]: (state, videoKind) => ({
    ...state,
    history: state.history.filter(video => video.type !== videoKind)
      .concat({
        type: videoKind,
        shouldBeWatched: false
      })
  })
});
